"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import {
  IconCheck,
  IconGlobe,
  IconChevronDown,
} from "@/components/icons";
import { usePathname, useRouter } from "@/i18n/navigation";
import { type Locale, routing } from "@/i18n/routing";

/**
 * Each locale's name written in its own language, so a speaker can always
 * recognise their own option regardless of the page's current language.
 */
const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  de: "Deutsch",
  ja: "日本語",
};

/**
 * Language picker for the header. Shows the active locale as a compact code
 * (e.g. "EN") and opens a menu of the locales in their own names. Choosing one
 * re-navigates to the same page under that locale, preserving the path. Client
 * component — it owns the menu open state and reads the active locale.
 *
 * `fullWidth` renders it as a left-aligned, full-width row for the mobile
 * drawer; omit it for the inline desktop trigger.
 */
export function LanguageSelect({ fullWidth = false }: { fullWidth?: boolean }) {
  const t = useTranslations("header");
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const select = (next: Locale) => {
    setAnchorEl(null);
    if (next !== locale) router.replace(pathname, { locale: next });
  };

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={t("language")}
        aria-haspopup="menu"
        aria-expanded={open}
        startIcon={<IconGlobe size={18} />}
        endIcon={<IconChevronDown size={15} />}
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          fontSize: "0.95rem",
          px: fullWidth ? 0 : 1.5,
          ...(fullWidth && { justifyContent: "flex-start", width: "100%" }),
        }}
      >
        {fullWidth ? LOCALE_NAMES[locale] : locale.toUpperCase()}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {routing.locales.map((value) => (
          <MenuItem
            key={value}
            selected={value === locale}
            onClick={() => select(value)}
            sx={{ gap: 2, minWidth: 160 }}
          >
            <ListItemText>{LOCALE_NAMES[value]}</ListItemText>
            {value === locale ? (
              <IconCheck size={16} style={{ color: "var(--color-primary)" }} />
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
